import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/base/SectionHeading";
import DramaCard from "@/components/base/DramaCard";
import { useSeo } from "@/hooks/useSeo";
import { relatedDramas, relatedGenres, relatedYears, relatedRatings } from "@/mocks/related";

const PAGE_SIZE = 6;

export default function RelatedPage() {
  const [genre, setGenre] = useState("全部");
  const [year, setYear] = useState("全部");
  const [rating, setRating] = useState("全部");
  const [visible, setVisible] = useState(PAGE_SIZE);

  useSeo({
    title: "善良的小姨子-相关推荐剧集合集",
    description:
      "善良的小姨子相关推荐剧集合集：精选家庭、爱情、治愈等题材的相似剧集，支持按类型、年份、评分筛选，帮助你发现更多值得追的好剧。",
    keywords: "善良的小姨子类似剧集,家庭情感剧推荐,治愈系剧集,高分剧集推荐",
  });

  const filtered = useMemo(() => {
    return relatedDramas.filter((item) => {
      const byGenre = genre === "全部" || item.genre === genre;
      const byYear = year === "全部" || String(item.year) === year;
      let byRating = true;
      if (rating === "9.0分以上") byRating = item.rating >= 9;
      else if (rating === "8.5分以上") byRating = item.rating >= 8.5;
      else if (rating === "8.0分以上") byRating = item.rating >= 8;
      return byGenre && byYear && byRating;
    });
  }, [genre, year, rating]);

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  const reset = () => {
    setGenre("全部");
    setYear("全部");
    setRating("全部");
    setVisible(PAGE_SIZE);
  };

  const selectClass =
    "w-full px-3 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 cursor-pointer";

  return (
    <SiteLayout>
      <section className="w-full px-4 md:px-8 pt-6 md:pt-8">
        <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-5">
          <Link to="/" className="hover:text-primary-600 cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700">相关推荐</span>
        </nav>

        <SectionHeading
          eyebrow="剧集推荐"
          title="相关剧集合集"
          desc="与《善良的小姨子》风格或题材相近的精选剧集，支持按类型、年份、评分筛选。"
        />

        <div className="rounded-lg bg-background-100 border border-background-200 p-4 md:p-5 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-500 mb-1.5">
                <i className="ri-price-tag-3-line mr-1"></i>类型
              </label>
              <select value={genre} onChange={(e) => { setGenre(e.target.value); setVisible(PAGE_SIZE); }} className={selectClass}>
                {relatedGenres.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-500 mb-1.5">
                <i className="ri-calendar-line mr-1"></i>年份
              </label>
              <select value={year} onChange={(e) => { setYear(e.target.value); setVisible(PAGE_SIZE); }} className={selectClass}>
                {relatedYears.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-500 mb-1.5">
                <i className="ri-star-line mr-1"></i>评分
              </label>
              <select value={rating} onChange={(e) => { setRating(e.target.value); setVisible(PAGE_SIZE); }} className={selectClass}>
                {relatedRatings.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={reset}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-secondary-500 text-secondary-950 text-sm font-medium hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i> 重置筛选
              </button>
            </div>
          </div>
        </div>

        <p className="text-sm text-foreground-500 mb-4">
          共找到 <span className="text-primary-600 font-medium">{filtered.length}</span> 部相关剧集
        </p>

        {shown.length === 0 ? (
          <div className="rounded-lg bg-background-100 border border-background-200 py-16 text-center">
            <i className="ri-emotion-normal-line text-3xl text-foreground-400"></i>
            <p className="text-sm text-foreground-500 mt-3">没有符合条件的剧集，试试调整筛选条件。</p>
            <button
              type="button"
              onClick={reset}
              className="mt-4 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i> 重置筛选条件
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
            {shown.map((item) => (
              <DramaCard
                key={item.id}
                to="/detail"
                cover={item.cover}
                title={item.title}
                genre={item.genre}
                year={item.year}
                rating={item.rating}
                duration={item.duration}
                summary={item.summary}
              />
            ))}
          </div>
        )}

        {hasMore && (
          <div className="flex justify-center mt-8">
            <button
              type="button"
              onClick={() => setVisible((v) => v + PAGE_SIZE)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50 border border-background-300 text-foreground-800 text-sm font-medium hover:bg-background-200 hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i> 加载更多（剩余 {filtered.length - visible} 部）
            </button>
          </div>
        )}
      </section>
    </SiteLayout>
  );
}